import { useState, type FormEvent } from 'react'
import { ApiError } from '../api/client'
import { useAction, useItem, useList } from '../api/queries'
import type { Client as Customer, Plant } from '../api/types'
import { DataTable, type Column } from '../components/DataTable'
import { Icon } from '../components/Icon'
import { Card, EmptyState, Field, LoadingState, Modal, PageHeader, SearchBox, StatusBadge } from '../components/ui'
import { useDebounced } from '../lib/hooks'

type ClientValues = Pick<Customer, 'legal_name' | 'nit' | 'address' | 'email' | 'contact_name' | 'phone' | 'website'>
type ClientCredentials = { username: string; password: string; created: boolean }
type ClientAccessValues = { password?: string }

const EMPTY_CLIENT: ClientValues = {
  legal_name: '', nit: '', address: '', email: '', contact_name: '', phone: '', website: '',
}

function ClientForm({ client, onClose }: { client?: Customer; onClose: () => void }) {
  const [values, setValues] = useState<ClientValues>(client ? {
    legal_name: client.legal_name,
    nit: client.nit,
    address: client.address,
    email: client.email,
    contact_name: client.contact_name,
    phone: client.phone,
    website: client.website,
  } : EMPTY_CLIENT)
  const [error, setError] = useState<ApiError | null>(null)
  const create = useAction<ClientValues, Customer>('post', '/api/clients/', ['/api/clients/'])
  const update = useAction<ClientValues, Customer>('patch', `/api/clients/${client?.id ?? 0}/`, ['/api/clients/'])
  const set = (key: keyof ClientValues) => (event: { target: { value: string } }) => {
    setValues((current) => ({ ...current, [key]: event.target.value }))
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    try {
      await (client ? update : create).mutateAsync(values)
      onClose()
    } catch (caught) {
      setError(caught as ApiError)
    }
  }

  const fields = error?.fieldErrors ?? {}
  const busy = create.isPending || update.isPending
  return (
    <Modal title={client ? 'Editar cliente' : 'Nuevo cliente'} onClose={onClose} wide>
      <form className="form-stack" onSubmit={submit}>
        <div className="form-grid form-grid--2">
          <Field label="Razón social" htmlFor="client-legal-name" error={fields.legal_name}>
            <input id="client-legal-name" required autoFocus value={values.legal_name} onChange={set('legal_name')} />
          </Field>
          <Field label="NIT" htmlFor="client-nit" error={fields.nit}>
            <input id="client-nit" required value={values.nit} onChange={set('nit')} />
          </Field>
        </div>
        <Field label="Dirección" htmlFor="client-address" error={fields.address}>
          <input id="client-address" value={values.address} onChange={set('address')} />
        </Field>
        <div className="form-grid form-grid--2">
          <Field label="Correo" htmlFor="client-email" error={fields.email}>
            <input id="client-email" type="email" value={values.email} onChange={set('email')} />
          </Field>
          <Field label="Teléfono" htmlFor="client-phone" error={fields.phone}>
            <input id="client-phone" type="tel" value={values.phone} onChange={set('phone')} />
          </Field>
        </div>
        <div className="form-grid form-grid--2">
          <Field label="Persona encargada" htmlFor="client-contact" error={fields.contact_name}>
            <input id="client-contact" value={values.contact_name} onChange={set('contact_name')} />
          </Field>
          <Field label="Sitio web" htmlFor="client-website" error={fields.website} hint="Incluye https://">
            <input id="client-website" type="url" placeholder="https://empresa.com" value={values.website} onChange={set('website')} />
          </Field>
        </div>
        {error && !Object.keys(fields).length ? <p role="alert" className="form-error">{error.message}</p> : null}
        <div className="modal-actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn--primary" disabled={busy}>{client ? 'Guardar cambios' : 'Crear cliente'}</button>
        </div>
      </form>
    </Modal>
  )
}

function ManagePlants({ client, onClose }: { client: Customer; onClose: () => void }) {
  const plants = useList<Plant>('/api/plants/', { page_size: 500 })
  const assign = useAction<{ plantId: number; client: number | null }, Plant>(
    'patch',
    (body) => `/api/plants/${body.plantId}/`,
    ['/api/plants/', '/api/clients/'],
    (body) => ({ client: body.client }),
  )
  const create = useAction<{ code: string; name: string; client: number }, Plant>(
    'post', '/api/plants/', ['/api/plants/', '/api/clients/'],
  )
  const [selected, setSelected] = useState('')
  const [creating, setCreating] = useState(false)
  const [plantValues, setPlantValues] = useState({ code: '', name: '' })
  const [error, setError] = useState<ApiError | null>(null)
  const all = plants.data?.results ?? []
  const assigned = all.filter((plant) => plant.client === client.id)
  const available = all.filter((plant) => plant.client === null || plant.client === undefined)

  async function assignSelected() {
    if (!selected) return
    setError(null)
    try {
      await assign.mutateAsync({ plantId: Number(selected), client: client.id })
      setSelected('')
    } catch (caught) {
      setError(caught as ApiError)
    }
  }

  async function createPlant(event: FormEvent) {
    event.preventDefault()
    setError(null)
    try {
      await create.mutateAsync({ ...plantValues, client: client.id })
      setPlantValues({ code: '', name: '' })
      setCreating(false)
    } catch (caught) {
      setError(caught as ApiError)
    }
  }

  const fields = error?.fieldErrors ?? {}
  return (
    <Modal title={`Plantas de ${client.legal_name}`} onClose={onClose} wide>
      <div className="plant-manager">
        <section className="plant-manager__section" aria-labelledby="assigned-plants-title">
          <div className="plant-manager__head">
            <div>
              <h3 id="assigned-plants-title">Plantas asignadas</h3>
              <p className="muted small">Estas plantas aparecerán asociadas a este cliente.</p>
            </div>
            <StatusBadge label={`${assigned.length} ${assigned.length === 1 ? 'planta' : 'plantas'}`} tone="info" />
          </div>
          {plants.isPending ? <LoadingState label="Cargando plantas…" /> : assigned.length ? (
            <ul className="assignment-list">
              {assigned.map((plant) => (
                <li key={plant.id}>
                  <span><strong>{plant.name}</strong><span className="mono muted">{plant.code}</span></span>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={assign.isPending}
                    onClick={() => void assign.mutateAsync({ plantId: plant.id, client: null }).catch((caught) => setError(caught as ApiError))}
                  >Desvincular</button>
                </li>
              ))}
            </ul>
          ) : <EmptyState title="Sin plantas asignadas" hint="Asigna una planta existente o crea una nueva." />}
        </section>

        <section className="plant-manager__section" aria-labelledby="add-plant-title">
          <div className="plant-manager__head">
            <div>
              <h3 id="add-plant-title">Agregar planta</h3>
              <p className="muted small">Usa una planta sin cliente o registra una nueva.</p>
            </div>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setCreating((value) => !value)}>
              <Icon name="plus" size={14} /> Nueva planta
            </button>
          </div>
          {available.length ? (
            <div className="assign-row">
              <label className="sr-only" htmlFor="available-plant">Planta existente</label>
              <select id="available-plant" value={selected} onChange={(event) => setSelected(event.target.value)}>
                <option value="">Selecciona una planta sin cliente</option>
                {available.map((plant) => <option key={plant.id} value={plant.id}>{plant.name} ({plant.code})</option>)}
              </select>
              <button type="button" className="btn btn--primary" disabled={!selected || assign.isPending} onClick={() => void assignSelected()}>Asignar</button>
            </div>
          ) : <p className="muted">No hay plantas sin cliente disponibles.</p>}

          {creating ? (
            <form className="form-stack plant-create" onSubmit={createPlant}>
              <div className="form-grid form-grid--2">
                <Field label="Código de planta" htmlFor="plant-code" error={fields.code} hint="Debe ser único.">
                  <input id="plant-code" required value={plantValues.code} onChange={(event) => setPlantValues({ ...plantValues, code: event.target.value.toUpperCase() })} />
                </Field>
                <Field label="Nombre de planta" htmlFor="plant-name" error={fields.name}>
                  <input id="plant-name" required value={plantValues.name} onChange={(event) => setPlantValues({ ...plantValues, name: event.target.value })} />
                </Field>
              </div>
              <div className="row">
                <button className="btn btn--primary btn--sm" disabled={create.isPending}>Crear y asignar</button>
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => setCreating(false)}>Cancelar</button>
              </div>
            </form>
          ) : null}
          {error && !Object.keys(fields).length ? <p role="alert" className="form-error">{error.message}</p> : null}
        </section>
      </div>
    </Modal>
  )
}

function ClientAccess({ client, onClose }: { client: Customer; onClose: () => void }) {
  const [replacement, setReplacement] = useState<ClientCredentials | null>(null)
  const [password, setPassword] = useState('')
  const [resetting, setResetting] = useState(false)
  const [copied, setCopied] = useState<'credentials' | 'invitation' | null>(null)
  const [copyError, setCopyError] = useState('')
  const [error, setError] = useState<ApiError | null>(null)
  const stored = useItem<ClientCredentials>(client.access ? `/api/clients/${client.id}/access/` : null)
  const provision = useAction<ClientAccessValues, ClientCredentials>(
    'post', `/api/clients/${client.id}/access/`, ['/api/clients/'],
  )
  const credentials = replacement ?? stored.data ?? null

  async function generate() {
    setError(null)
    setCopied(null)
    try {
      setReplacement(await provision.mutateAsync(password ? { password } : {}))
      setPassword('')
      setResetting(false)
    } catch (caught) {
      setError(caught as ApiError)
    }
  }

  const invitation = credentials ? [
    'Bienvenido a su sistema de identificación e inspección de equipos.',
    '',
    `Ingrese aquí: ${window.location.origin}/login`,
    `Usuario: ${credentials.username}`,
    `Contraseña: ${credentials.password}`,
  ].join('\n') : ''

  async function copyText(value: string, kind: 'credentials' | 'invitation') {
    setCopyError('')
    try {
      await navigator.clipboard.writeText(value)
      setCopied(kind)
    } catch {
      setCopyError('No fue posible copiar la invitación. Selecciona el texto y cópialo manualmente.')
    }
  }

  return (
    <Modal title={`Acceso de ${client.legal_name}`} onClose={onClose}>
      <div className="form-stack">
        <p className="muted">Este usuario tendrá acceso de solo lectura y únicamente podrá consultar las plantas asociadas a este cliente.</p>
        {client.access && stored.isPending && !replacement ? <LoadingState label="Cargando credenciales…" /> : null}
        {stored.error && !replacement ? <p role="alert" className="form-error">{stored.error.message}</p> : null}
        {credentials ? (
          <>
            <div className="callout callout--warn" role="status">
              <strong>Credenciales asignadas</strong>
              <p className="small">Visibles únicamente para administradores autorizados.</p>
              <dl className="credential-list">
                <div><dt>Usuario</dt><dd className="mono">{credentials.username}</dd></div>
                <div><dt>Contraseña</dt><dd className="mono">{credentials.password}</dd></div>
              </dl>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => void copyText(`Usuario: ${credentials.username}\nContraseña: ${credentials.password}`, 'credentials')}
              >{copied === 'credentials' ? 'Credenciales copiadas' : 'Copiar usuario y contraseña'}</button>
            </div>
            <div className="invitation-preview">
              <strong>Invitación para el cliente</strong>
              <pre>{invitation}</pre>
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => void copyText(invitation, 'invitation')}>
                {copied === 'invitation' ? 'Invitación copiada' : 'Copiar invitación de bienvenida'}
              </button>
            </div>
          </>
        ) : null}
        {(!client.access || resetting) ? (
          <Field
            label={client.access ? 'Nueva contraseña' : 'Contraseña personalizada'}
            htmlFor="client-access-password"
            error={error?.fieldErrors?.password}
            hint="Opcional. Mínimo 10 caracteres; déjala vacía para generar una contraseña corta."
          >
            <input
              id="client-access-password"
              type="password"
              autoComplete="new-password"
              minLength={10}
              maxLength={128}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>
        ) : null}
        {error && !error.fieldErrors?.password ? <p role="alert" className="form-error">{error.message}</p> : null}
        {copyError ? <p role="alert" className="form-error">{copyError}</p> : null}
        <div className="modal-actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>Cerrar</button>
          {client.access && !resetting ? (
            <button type="button" className="btn btn--ghost" onClick={() => setResetting(true)}>
              Restablecer contraseña
            </button>
          ) : null}
          {(!client.access || resetting) ? (
            <button type="button" className="btn btn--primary" disabled={provision.isPending} onClick={() => void generate()}>
              {client.access ? (password ? 'Guardar nueva contraseña' : 'Generar otra contraseña aleatoria') : 'Generar usuario y contraseña'}
            </button>
          ) : null}
        </div>
      </div>
    </Modal>
  )
}

export function ClientsPage() {
  const [search, setSearch] = useState('')
  const query = useDebounced(search)
  const clients = useList<Customer>('/api/clients/', { page_size: 200, search: query })
  const [formId, setFormId] = useState<number | 'new' | null>(null)
  const [plantsId, setPlantsId] = useState<number | null>(null)
  const [accessId, setAccessId] = useState<number | null>(null)
  const rows = clients.data?.results ?? []
  const editing = typeof formId === 'number' ? rows.find((client) => client.id === formId) : undefined
  const managing = plantsId === null ? undefined : rows.find((client) => client.id === plantsId)
  const accessClient = accessId === null ? undefined : rows.find((client) => client.id === accessId)

  const columns: Column<Customer>[] = [
    { key: 'client', header: 'Razón social', cell: (client) => <><strong>{client.legal_name}</strong><div className="muted mono">{client.nit}</div></> },
    { key: 'contact', header: 'Contacto', cell: (client) => <div className="client-contact"><span>{client.contact_name || '—'}</span>{client.email ? <a href={`mailto:${client.email}`}>{client.email}</a> : null}{client.phone ? <a href={`tel:${client.phone}`}>{client.phone}</a> : null}</div> },
    { key: 'address', header: 'Dirección', cell: (client) => client.address || '—', priority: 'low' },
    { key: 'web', header: 'Sitio web', cell: (client) => client.website ? <a href={client.website} target="_blank" rel="noreferrer">Abrir sitio <Icon name="arrow-up-right" size={13} /></a> : '—', priority: 'low' },
    { key: 'plants', header: 'Plantas', cell: (client) => <StatusBadge label={String(client.plants.length)} tone={client.plants.length ? 'info' : 'neutral'} /> , align: 'center' },
    { key: 'access', header: 'Acceso', cell: (client) => <StatusBadge label={client.access ? client.access.username : 'Sin usuario'} tone={client.access?.is_active ? 'ok' : 'neutral'} /> },
    { key: 'actions', header: 'Acciones', cell: (client) => <div className="row"><button type="button" className="btn btn--ghost btn--sm" onClick={() => setFormId(client.id)}>Editar</button><button type="button" className="btn btn--ghost btn--sm" onClick={() => setAccessId(client.id)}>Acceso</button><button type="button" className="btn btn--primary btn--sm" onClick={() => setPlantsId(client.id)}>Administrar plantas</button></div> },
  ]

  return (
    <div className="page">
      <PageHeader
        title="Clientes"
        subtitle="Administra los datos de cada empresa y las plantas que tiene asignadas"
        actions={<button type="button" className="btn btn--primary" onClick={() => setFormId('new')}><Icon name="plus" size={16} /> Nuevo cliente</button>}
      />
      <Card actions={<SearchBox label="Buscar clientes" placeholder="Razón social, NIT o contacto" value={search} onChange={setSearch} />}>
        <DataTable
          caption="Clientes"
          columns={columns}
          rows={clients.data?.results}
          loading={clients.isPending}
          error={clients.error}
          onRetry={() => void clients.refetch()}
          rowKey={(client) => client.id}
          emptyTitle={query ? 'No se encontraron clientes' : 'Aún no hay clientes'}
          emptyHint={query ? 'Prueba con otro término de búsqueda.' : 'Crea el primer cliente para empezar a asignarle plantas.'}
          emptyAction={!query ? <button type="button" className="btn btn--primary" onClick={() => setFormId('new')}>Nuevo cliente</button> : undefined}
        />
      </Card>
      {formId !== null ? <ClientForm client={editing} onClose={() => setFormId(null)} /> : null}
      {managing ? <ManagePlants client={managing} onClose={() => setPlantsId(null)} /> : null}
      {accessClient ? <ClientAccess client={accessClient} onClose={() => setAccessId(null)} /> : null}
    </div>
  )
}
